/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid } from '@coursera/cds-core';

import { useConsumerMyPageHeader } from 'bundles/composable-page-header-consumer/contexts/ConsumerMyPageHeaderContext';
import EnterpriseOrgScopedSearchBarGridItem from 'bundles/composable-page-header-enterprise/components/EnterpriseOrgScopedSearchBarGridItem';
import EnterpriseSearchBarLegacyGridItem from 'bundles/composable-page-header-enterprise/components/EnterpriseSearchBarLegacyGridItem';
import {
  getEnterpriseLogoLabel,
  getLinkForEnterpriseProgram,
} from 'bundles/composable-page-header-enterprise/utils/enterpriseLogoUtils';
import HelpDropdownGridItem from 'bundles/composable-page-header/components/HelpDropdownGridItem';
import MainNavigationWrapper from 'bundles/composable-page-header/components/MainNavigationWrapper';
import NavAiSparkleGridItem from 'bundles/composable-page-header/components/grid-items/NavAiSparkleGridItem';
import NotificationsLegacyGridItem from 'bundles/composable-page-header/components/grid-items/NotificationsLegacyGridItem';
import CourseraHomeLogo from 'bundles/composable-page-header/components/grid-items/home-logo/CourseraHomeLogo';
import HomeLogoGridItemModule from 'bundles/composable-page-header/components/grid-items/home-logo/HomeLogoGridItemModule';
import MobileAccountMenuGridItem from 'bundles/composable-page-header/components/grid-items/mobile/MobileAccountMenuGridItem';
import MobileMenuIconGridItem from 'bundles/composable-page-header/components/grid-items/mobile/MobileMenuIconGridItem';
import AccountMenuConfig from 'bundles/composable-page-header/components/mobile-menu-items/AccountMenuConfig';
import useShouldShowHelpDropdown from 'bundles/composable-page-header/hooks/useShouldShowHelpDropdown';
import LoggedInMobileMenuConfigWithMetaNav from 'bundles/composable-page-header/module-renderers/LoggedInMobileMenuConfigWithMetaNav';
import useUnifiedEnterpriseSearch from 'bundles/search-common/hooks/useUnifiedEnterpriseSearch';
import { useIsOrgScopedHome } from 'bundles/unified-common/hooks/useIsOrgScopedHome';

type Props = {
  showDiscoveryElements?: boolean;
  showSearch?: boolean;
};

const styles = {
  gridItemContainer: css`
    display: flex;
    align-items: center;
  `,
};

const EnterpriseLearnerOnConsumerMobileMainNav = ({ showDiscoveryElements = true, showSearch = true }: Props) => {
  const {
    enrolledDegreePrograms,
    enrolledEnterprisePrograms,
    isCourseraPlusLearner,
    chosenDegree,
    chosenEnterpriseProgram,
    hasEnrolledPrograms,
    naptimeManagedThirdPartyOrganizations,
    hasCheatingIncidents,
    canViewEducatorAdminLink,
    courseraPlusSubscriptionCycle,
    naptimeChosenOrganization,
    naptimeOrganizationsWithProgramEnrollments,
  } = useConsumerMyPageHeader();
  const shouldShowHelpDropdown = useShouldShowHelpDropdown();

  const isOrgScoped = useIsOrgScopedHome();
  const showUnifiedSearch = useUnifiedEnterpriseSearch();

  if (!chosenEnterpriseProgram) {
    return null;
  }

  return (
    <MainNavigationWrapper>
      <Grid container justifyContent="space-between" alignItems="center" wrap="nowrap">
        <Grid item css={styles.gridItemContainer}>
          <MobileMenuIconGridItem
            menuConfig={
              <LoggedInMobileMenuConfigWithMetaNav
                showDiscoveryElements={showDiscoveryElements}
                enrolledDegreePrograms={enrolledDegreePrograms}
                enrolledEnterprisePrograms={enrolledEnterprisePrograms}
                isCourseraPlusLearner={isCourseraPlusLearner}
                chosenDegree={chosenDegree}
                chosenEnterpriseProgram={chosenEnterpriseProgram}
                hasEnrolledPrograms={!!hasEnrolledPrograms}
                naptimeManagedThirdPartyOrganizations={naptimeManagedThirdPartyOrganizations}
                hasCheatingIncidents={hasCheatingIncidents}
                canViewEducatorAdminLink={canViewEducatorAdminLink}
                courseraPlusSubscriptionCycle={courseraPlusSubscriptionCycle}
              />
            }
            homeLogo={
              <HomeLogoGridItemModule
                href={getLinkForEnterpriseProgram(chosenEnterpriseProgram)}
                logos={[<CourseraHomeLogo key="1" />]}
                ariaLabel={getEnterpriseLogoLabel(chosenEnterpriseProgram)}
              />
            }
          />
          <HomeLogoGridItemModule
            href={getLinkForEnterpriseProgram(chosenEnterpriseProgram)}
            logos={[<CourseraHomeLogo key="1" />]}
            ariaLabel={getEnterpriseLogoLabel(chosenEnterpriseProgram)}
          />
        </Grid>
        <Grid item css={styles.gridItemContainer}>
          {shouldShowHelpDropdown && <HelpDropdownGridItem disableChatSsr={true} />}
          <NavAiSparkleGridItem />
          <NotificationsLegacyGridItem />
          {showSearch && showDiscoveryElements && (
            <Grid item>
              {showUnifiedSearch ? (
                <EnterpriseOrgScopedSearchBarGridItem
                  thirdPartyOrganizationSlug={chosenEnterpriseProgram.naptimeThirdPartyOrganization?.slug}
                  enrolledEnterprisePrograms={enrolledEnterprisePrograms}
                  chosenProgramSlug={chosenEnterpriseProgram.slug}
                />
              ) : (
                <EnterpriseSearchBarLegacyGridItem currentProgram={chosenEnterpriseProgram} />
              )}
            </Grid>
          )}
          {isOrgScoped && (
            <MobileAccountMenuGridItem
              isCourseraPlusLearner={!!isCourseraPlusLearner}
              menuConfig={
                <AccountMenuConfig
                  naptimeManagedThirdPartyOrganizations={naptimeManagedThirdPartyOrganizations}
                  canViewEducatorAdminLink={canViewEducatorAdminLink}
                  hasCheatingIncidents={hasCheatingIncidents}
                  currentOrganization={naptimeChosenOrganization}
                  currentDegree={chosenDegree}
                  degrees={enrolledDegreePrograms}
                  organizations={naptimeOrganizationsWithProgramEnrollments}
                />
              }
            />
          )}
        </Grid>
      </Grid>
    </MainNavigationWrapper>
  );
};

export default EnterpriseLearnerOnConsumerMobileMainNav;
